import { useRef, useState } from 'react'
import { useEffectWithoutInitialRender } from './useEffectWithoutInitialRender'

/**
 * Debounces a filters object and calls onSearch delayMs after the filters stop changing.
 * Skips the initial render (so it doesn't fire on mount) and exposes an in-progress
 * flag for a loading indicator. Cancels the pending call if filters change again before
 * delayMs elapses.
 *
 * Also returns flush(), which cancels the pending timer and runs onSearch immediately -
 * e.g. for a search-now icon button or Enter-to-search shortcut.
 */
export function useDebouncedFilters<TFilters extends Record<string, unknown>>(
	filters: TFilters,
	onSearch: () => void,
	delayMs = 300
) {
	const [searchInProgress, setSearchInProgress] = useState(false)
	const timerRef = useRef<ReturnType<typeof setTimeout>>()
	const onSearchRef = useRef(onSearch)
	onSearchRef.current = onSearch

	useEffectWithoutInitialRender(() => {
		const hasTextFilter = Object.values(filters).some(
			(value) => typeof value === 'string' && value !== ''
		)
		if (hasTextFilter) {
			setSearchInProgress(true)
		}

		const timer = setTimeout(() => {
			onSearchRef.current()
			setSearchInProgress(false)
			timerRef.current = undefined
		}, delayMs)
		timerRef.current = timer

		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters])

	const flush = () => {
		clearTimeout(timerRef.current)
		timerRef.current = undefined
		onSearchRef.current()
		setSearchInProgress(false)
	}

	return { searchInProgress, flush }
}
